// Enhanced sound effects utility for gamification
class SoundEffects {
  constructor() {
    this.enabled = localStorage.getItem('soundEffectsEnabled') !== 'false';
    this.audioContext = null;
    this.audioContextInitialized = false;
    this._userGestureListenerAttached = false;
    this._attachUserGestureListener();
  }

  _attachUserGestureListener() {
    if (this._userGestureListenerAttached || typeof document === 'undefined') return;
    this._userGestureListenerAttached = true;
    const initOnce = () => {
      this.initAudioContext();
      ['click', 'keydown', 'touchstart'].forEach(ev => document.removeEventListener(ev, initOnce));
    };
    ['click', 'keydown', 'touchstart'].forEach(ev => document.addEventListener(ev, initOnce, { once: true, passive: true }));
  }

  async initAudioContext() {
    if (this.audioContextInitialized && this.audioContext) {
      if (this.audioContext.state === 'suspended') {
        try {
          await this.audioContext.resume();
        } catch (error) {
          console.warn('Failed to resume audio context:', error);
        }
      }
      return;
    }

    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.audioContextInitialized = true;
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
    } catch (error) {
      console.warn('Audio context not available:', error);
    }
  }

  setEnabled(enabled) {
    this.enabled = enabled;
    localStorage.setItem('soundEffectsEnabled', enabled.toString());
  }

  // Play a beep sound with enhanced options
  async playBeep(frequency = 800, duration = 100, type = 'sine', volume = 0.3) {
    if (!this.enabled) return;

    // Only use audio after a user gesture (browser policy); avoid creating AudioContext on page load
    if (!this.audioContextInitialized || !this.audioContext) return;

    try {
      // Ensure audio context is running
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = type;

      gainNode.gain.setValueAtTime(volume, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + duration / 1000);

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + duration / 1000);
    } catch (error) {
      console.warn('Failed to play beep:', error);
    }
  }

  // Play a chord (multiple frequencies)
  playChord(frequencies, duration = 200, type = 'sine', volume = 0.2) {
    if (!this.enabled || !this.audioContext) return;
    frequencies.forEach(freq => {
      this.playBeep(freq, duration, type, volume);
    });
  }

  // Play success sound (enhanced)
  playSuccess() {
    this.playBeep(600, 100, 'sine');
    setTimeout(() => this.playBeep(800, 150, 'sine'), 100);
    setTimeout(() => this.playBeep(1000, 100, 'sine'), 250);
  }

  // Play error sound (enhanced)
  playError() {
    this.playBeep(300, 200, 'sawtooth', 0.4);
    setTimeout(() => this.playBeep(250, 150, 'sawtooth', 0.3), 150);
  }

  // Play coin sound (for transactions) - enhanced
  playCoin() {
    this.playBeep(500, 80, 'sine');
    setTimeout(() => this.playBeep(700, 100, 'sine'), 80);
    setTimeout(() => this.playBeep(900, 80, 'sine'), 180);
  }

  // Play level up sound (enhanced)
  playLevelUp() {
    // Ascending notes with fanfare
    [400, 500, 600, 700, 800, 900, 1000].forEach((freq, i) => {
      setTimeout(() => this.playBeep(freq, 150, 'sine', 0.25), i * 80);
    });
    // Final celebratory chord
    setTimeout(() => this.playChord([600, 800, 1000], 300, 'sine', 0.2), 600);
  }

  // Play achievement sound (enhanced)
  playAchievement() {
    [600, 800, 1000, 1200].forEach((freq, i) => {
      setTimeout(() => this.playBeep(freq, 200, 'sine', 0.3), i * 120);
    });
    setTimeout(() => this.playChord([800, 1000, 1200], 400, 'sine', 0.2), 500);
  }

  // Play buy sound (enhanced)
  playBuy() {
    this.playBeep(400, 100, 'sine');
    setTimeout(() => this.playBeep(600, 150, 'sine'), 100);
    setTimeout(() => this.playBeep(800, 100, 'sine'), 250);
  }

  // Play sell sound (enhanced)
  playSell() {
    this.playBeep(600, 100, 'sine');
    setTimeout(() => this.playBeep(400, 150, 'sine'), 100);
    setTimeout(() => this.playBeep(300, 100, 'sine'), 250);
  }

  // NEW: Play click sound
  playClick() {
    this.playBeep(800, 50, 'sine', 0.15);
  }

  // NEW: Play hover sound
  playHover() {
    this.playBeep(1000, 30, 'sine', 0.1);
  }

  // NEW: Play notification sound
  playNotification() {
    this.playBeep(600, 100, 'sine', 0.25);
    setTimeout(() => this.playBeep(800, 100, 'sine', 0.25), 150);
  }

  // NEW: Play win sound
  playWin() {
    [523, 659, 784, 1047].forEach((freq, i) => {
      setTimeout(() => this.playBeep(freq, 200, 'sine', 0.3), i * 150);
    });
  }

  // NEW: Play loss sound
  playLoss() {
    this.playBeep(200, 300, 'sawtooth', 0.3);
    setTimeout(() => this.playBeep(150, 250, 'sawtooth', 0.25), 200);
  }

  // NEW: Play correct answer sound
  playCorrect() {
    this.playBeep(800, 100, 'sine', 0.3);
    setTimeout(() => this.playBeep(1000, 150, 'sine', 0.3), 100);
  }

  // NEW: Play wrong answer sound
  playWrong() {
    this.playBeep(300, 150, 'sawtooth', 0.3);
    setTimeout(() => this.playBeep(250, 150, 'sawtooth', 0.25), 100);
  }

  // NEW: Play page transition sound
  playPageTransition() {
    this.playBeep(600, 80, 'sine', 0.15);
  }

  // NEW: Play unlock sound
  playUnlock() {
    [400, 500, 600].forEach((freq, i) => {
      setTimeout(() => this.playBeep(freq, 150, 'sine', 0.25), i * 100);
    });
  }

  // NEW: Play sparkle sound
  playSparkle() {
    const frequencies = [800, 1000, 1200, 1000, 800];
    frequencies.forEach((freq, i) => {
      setTimeout(() => this.playBeep(freq, 60, 'sine', 0.2), i * 50);
    });
  }

  // NEW: Play pop sound
  playPop() {
    this.playBeep(1000, 100, 'sine', 0.2);
  }

  // NEW: Play whoosh sound
  playWhoosh() {
    // Frequency sweep
    let freq = 200;
    const interval = setInterval(() => {
      if (freq > 1000) {
        clearInterval(interval);
        return;
      }
      this.playBeep(freq, 30, 'sine', 0.1);
      freq += 50;
    }, 30);
  }
}

export default new SoundEffects();

